import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  LayoutDashboard, 
  Users, 
  ClipboardList, 
  BarChart3, 
  Bell, 
  LogOut, 
  Menu, 
  ChevronRight,
  ChevronDown,
  Image,
  Target,
  Sun,
  Moon,
  Briefcase,
  Sparkles,
  Upload,
  Building2,
  UserCog,
  Trophy,
  LineChart,
  CalendarRange,
  CalendarDays,
  Award as AwardIcon
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

const ARRISE_LOGO = "https://customer-assets.emergentagent.com/job_fb611978-98d1-42d5-bc77-019f23314229/artifacts/ymlim5gt_arrise-logomark.webp";

const AdminLayout = ({ children }) => {
  const { user, logout, isManager } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/org-chart', label: 'Org Chart', icon: Building2 },
    { path: '/roles', label: 'Roles & Duties', icon: Briefcase },
    { 
      path: '/scores', 
      label: 'Weekly Scores', 
      icon: ClipboardList,
      children: [
        { path: '/scores-6month', label: '6 Months Score', icon: CalendarRange },
        { path: '/scores-12month', label: '12 Months Score', icon: CalendarDays },
      ]
    },
    { path: '/achievements', label: 'Achievements', icon: Sparkles },
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/performance', label: 'Team Performance', icon: BarChart3 },
    { path: '/stack-ranking', label: 'Stack Ranking', icon: Trophy },
    { path: '/awards', label: 'Hall of Fame', icon: AwardIcon },
    { path: '/analytics', label: 'Analytics', icon: LineChart },
    { path: '/goals', label: 'Goals & Notes', icon: Target },
    { path: '/screenshots', label: 'Screenshots', icon: Image },
    { path: '/alerts', label: 'Alerts', icon: Bell },
    { path: '/users', label: 'User Management', icon: UserCog, adminOnly: true },
    { path: '/designers', label: 'Designers', icon: Users, managerOnly: true },
    { path: '/bulk-import', label: 'Bulk Import', icon: Upload, managerOnly: true },
  ];

  // Flattened list for top-bar title lookup
  const flatNavItems = navItems.flatMap(item => item.children ? [item, ...item.children] : [item]);

  // Track which parent menus are expanded. Auto-expand if a child route is active.
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const initial = {};
    navItems.forEach(item => {
      if (item.children && item.children.some(c => location.pathname.startsWith(c.path))) {
        initial[item.path] = true;
      }
    });
    return initial;
  });

  const toggleMenu = (path) => {
    setExpandedMenus(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`min-h-screen flex ${isDark ? 'dark bg-slate-900' : 'bg-[#F6F6F6]'}`}>
      {/* Mobile Menu Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed lg:static inset-y-0 left-0 w-64 z-50 transform transition-transform duration-300 lg:transform-none",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="h-full sidebar flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <img src={ARRISE_LOGO} alt="ARRISE" className="w-10 h-10 rounded-xl" />
              <div>
                <h1 className="text-white font-bold text-lg font-outfit">ARRISE</h1>
                <p className="text-white/60 text-xs">Performance Tracker</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              if (item.managerOnly && !isManager()) return null;
              if (item.adminOnly && user?.role !== 'admin') return null;
              
              const Icon = item.icon;
              const hasChildren = !!item.children?.length;
              const childActive = hasChildren && item.children.some(c => location.pathname.startsWith(c.path));
              const selfActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && !hasChildren && location.pathname.startsWith(item.path)) ||
                (hasChildren && location.pathname === item.path);
              const isActive = selfActive;
              const isExpanded = !!expandedMenus[item.path] || childActive;

              if (hasChildren) {
                return (
                  <div key={item.path}>
                    <div className="flex items-stretch">
                      <Link
                        to={item.path}
                        data-testid={`nav-${item.path.slice(1)}`}
                        className={cn(
                          "sidebar-link flex items-center gap-3 px-4 py-3 rounded-xl text-white/80 hover:text-white transition-all flex-1",
                          isActive && "active bg-white/10 text-white border-l-4 border-[#CCFF00]"
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className={cn("w-5 h-5", isActive && "text-[#CCFF00]")} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => toggleMenu(item.path)}
                        data-testid={`nav-toggle-${item.path.slice(1)}`}
                        className="px-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label={`Toggle ${item.label} submenu`}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childIsActive = location.pathname === child.path || location.pathname.startsWith(child.path);
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              data-testid={`nav-${child.path.slice(1)}`}
                              className={cn(
                                "sidebar-link flex items-center gap-3 px-3 py-2 rounded-lg text-white/70 hover:text-white text-sm transition-all",
                                childIsActive && "active bg-white/10 text-white border-l-4 border-[#CCFF00]"
                              )}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <ChildIcon className={cn("w-4 h-4", childIsActive && "text-[#CCFF00]")} />
                              <span className="font-medium">{child.label}</span>
                              {childIsActive && <ChevronRight className="w-4 h-4 ml-auto text-[#CCFF00]" />}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={cn(
                    "sidebar-link flex items-center gap-3 px-4 py-3 rounded-xl text-white/80 hover:text-white transition-all",
                    isActive && "active bg-white/10 text-white border-l-4 border-[#CCFF00]"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className={cn("w-5 h-5", isActive && "text-[#CCFF00]")} />
                  <span className="font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto text-[#CCFF00]" />}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-[#CCFF00] flex items-center justify-center">
                <span className="text-[#081117] font-bold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                <p className="text-white/60 text-xs capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full mt-2 flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className={cn(
          "border-b sticky top-0 z-30",
          isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-100"
        )}>
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className={cn(
                "lg:hidden p-2 rounded-lg",
                isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
              )}
              data-testid="mobile-menu-btn"
            >
              <Menu className={cn("w-6 h-6", isDark ? "text-slate-300" : "text-slate-600")} />
            </button>
            
            <div className="hidden lg:block">
              <h2 className={cn(
                "text-xl font-semibold font-outfit",
                isDark ? "text-white" : "text-slate-800"
              )}>
                {flatNavItems.find(item => location.pathname.startsWith(item.path))?.label || 'Dashboard'}
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-header"
                className="rounded-full"
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-slate-300" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </Button>
              <Link 
                to="/alerts"
                data-testid="alerts-header-btn"
                className={cn(
                  "relative p-2 rounded-lg",
                  isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
                )}
              >
                <Bell className={cn("w-5 h-5", isDark ? "text-slate-300" : "text-slate-600")} />
              </Link>
              <div className={cn(
                "hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full",
                isDark ? "bg-slate-700" : "bg-slate-100"
              )}>
                <span className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-600")}>Hi,</span>
                <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-slate-800")}>{user?.name}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className={cn("flex-1 p-4 lg:p-8", isDark ? "bg-slate-900" : "")}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
